import { useState } from "react";

import { useBillboardOrganizationService } from "pages/[tenant]/browse/hooks/useBillboardOrganizationService";
import { IOrganizationBillboardContent } from "pages/[tenant]/browse/types";
import {
  getSection,
  getSectionContent,
} from "pages/[tenant]/browse/utils/requests";
import { IGroup } from "pages/[tenant]/g/@types";

import { useOrganizationConfig } from "shared/hooks/useOrganizationConfig";
import { getAllSettledPromiseValue } from "shared/utils/getAllSettledPromiseValue";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import { IBillboardHighlight } from "@Console/billboard/types";
import { useOrganizationService } from "@Console/organizations/[organization_id]/hooks/useOrganizationService";
import { IGetOrganizationInfo } from "@Console/organizations/[organization_id]/types";
import { captureException, captureMessage } from "@sentry/nextjs";

export const useBillboardOrganizationContent = () => {
  const [
    isLoadingOrganizationBillboardContent,
    setIsLoadingOrganizationBillboardContent,
  ] = useState(false);
  const [organizationBillboardContent, setOrganizationBillboardContent] =
    useState<IOrganizationBillboardContent>({
      highlights: [],
      sections: [],
      sectionContent: [],
    });
  const {
    state: { tenancy },
    setState,
  } = useGlobalContext();

  const { getOrganizationInfo } = useOrganizationService();

  const { getOrganizationFromCookies } = useOrganizationConfig();
  const organizationInCookie = getOrganizationFromCookies();
  const {
    getCachedBillboardOrganizationContent,
    saveBillboardOrganizationContentInCache,
  } = useBillboardOrganizationService();

  const [requestsProgressRateConclusion, setRequestsProgressRateConclusion] =
    useState(0);

  const handleLoadOrganizationBillboardContent = async () => {
    try {
      setIsLoadingOrganizationBillboardContent(true);

      const cachedContent = await getCachedBillboardOrganizationContent({
        organizationId: organizationInCookie?.id,
        tenantUuid: tenancy?.uuid,
      });
      if (cachedContent) {
        setOrganizationBillboardContent({
          highlights: cachedContent?.highlights,
          sectionContent: cachedContent?.sectionContent,
          sections: cachedContent?.sections,
        });

        setRequestsProgressRateConclusion(100);
        return;
      }

      const [organizationResult, sectionsResult] = await Promise.allSettled([
        getOrganizationInfo(
          organizationInCookie?.id?.toString(),
          setRequestsProgressRateConclusion((prev) => prev + 10),
        ),
        getSection(organizationInCookie?.id?.toString(), () =>
          setRequestsProgressRateConclusion((prev) => prev + 10),
        ),
      ]);

      const organization = getAllSettledPromiseValue(
        organizationResult,
        {},
      ) as IGetOrganizationInfo;
      const sections = getAllSettledPromiseValue(sectionsResult, []);
      const highlights = [
        {
          content: organization?.billboard_group as unknown as IGroup,
        },
      ] as IBillboardHighlight[];

      const singleSectionRequestProgressValue = Math.round(
        (100 - 20) / sections?.length,
      );

      const sectionContentResult = await Promise.allSettled(
        sections?.map((section) =>
          getSectionContent({
            sectionId: section?.id,
            organizationId: organizationInCookie?.id?.toString(),
            onFinishCallback: () =>
              setRequestsProgressRateConclusion(
                (prev) => prev + singleSectionRequestProgressValue,
              ),
          }),
        ),
      );

      const sectionContent = sectionContentResult?.map((sectionResult) =>
        getAllSettledPromiseValue(sectionResult, []),
      );

      setOrganizationBillboardContent({
        highlights,
        sectionContent,
        sections,
      });

      saveBillboardOrganizationContentInCache({
        organizationId: organizationInCookie?.id,
        payload: {
          highlights,
          sectionContent,
          sections,
        },
        tenantUuid: tenancy?.uuid,
      });
      setRequestsProgressRateConclusion((prev) => prev + (100 - prev));
    } catch (error) {
      console.log("Error (handleLoadOrganizationBillboardContent): ", error);
      captureException(error);
      captureMessage("Erro (handleLoadOrganizationBillboardContent)");

      openNotificationWithIcon(
        "error",
        "Não foi possível carregar o conteúdo no momento. Por favor, tente novamente mais tarde.",
      );
    } finally {
      setIsLoadingOrganizationBillboardContent(false);
    }
  };

  return {
    isLoadingOrganizationBillboardContent,
    organizationBillboardContent,
    handleLoadOrganizationBillboardContent,
    requestsProgressRateConclusion,
  };
};
